import {
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import { cx } from '../lib/cx';
import styles from './Field.module.css';
import { Select, type SelectProps } from './Select';

type FieldShellProps = {
  label: string;
  /** Visually hidden label, for compact rows where the context is obvious. */
  hideLabel?: boolean;
  hint?: string;
  error?: string | null;
  className?: string;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
};

function FieldShell({ label, hideLabel, hint, error, className, children }: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx(styles.field, className)}>
      <label htmlFor={id} className={cx(styles.label, hideLabel && 'visually-hidden')}>
        {label}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint && !error ? (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className={styles.error}>
          {error}
        </p>
      ) : null}
    </div>
  );
}

type Common = { label: string; hideLabel?: boolean; hint?: string; error?: string | null };

export function TextField({
  label,
  hideLabel,
  hint,
  error,
  className,
  ...input
}: Common & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <FieldShell {...{ label, hideLabel, hint, error, className }}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          className={styles.control}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...input}
        />
      )}
    </FieldShell>
  );
}

export function TextAreaField({
  label,
  hideLabel,
  hint,
  error,
  className,
  ...textarea
}: Common & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <FieldShell {...{ label, hideLabel, hint, error, className }}>
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          className={cx(styles.control, styles.textarea)}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...textarea}
        />
      )}
    </FieldShell>
  );
}

/** A yes/no choice: the native checkbox, the label beside it, an optional hint below. */
export function CheckboxField({
  label,
  hint,
  className,
  ...input
}: { label: string; hint?: string } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const id = useId();
  return (
    <div className={cx(styles.checkField, className)}>
      <input
        id={id}
        type="checkbox"
        className={styles.checkbox}
        aria-describedby={hint ? `${id}-hint` : undefined}
        {...input}
      />
      <label htmlFor={id} className={styles.checkLabel}>
        {label}
      </label>
      {hint ? (
        <p id={`${id}-hint`} className={cx(styles.hint, styles.checkHint)}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A choice from a list. Takes <option> and <optgroup> children like a native <select>, but the
 * list is ours (see Select): it stays on screen, can be searched and follows the identity.
 */
export function SelectField({
  label,
  hideLabel,
  hint,
  error,
  className,
  ...select
}: Common & Omit<SelectProps, 'id' | 'describedBy' | 'invalid'> & { className?: string }) {
  return (
    <FieldShell {...{ label, hideLabel, hint, error, className }}>
      {({ id, describedBy, invalid }) => (
        <Select id={id} describedBy={describedBy} invalid={invalid} {...select} />
      )}
    </FieldShell>
  );
}
